import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getDepositLedgerAll } from '../../actions/depositLedger';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Form, 
    Button, 
    Table, 
    Badge, 
    Alert, 
    Spinner,
    Toast,
    ToastContainer,
    Pagination
} from 'react-bootstrap';
import "bootstrap/dist/css/bootstrap.min.css";

/**
 * DepositLedger Component with Enhanced URL Query Parameter Support
 *
 * This component now supports extracting all query parameters from the URL:
 * - deliveryType: Filter by delivery type (vending_machine, delivery, pickup)
 * - startDate: Filter by start date (YYYY-MM-DD)
 * - endDate: Filter by end date (YYYY-MM-DD)
 * - latest: Sort order (true/false)
 * - page: Page number for pagination
 *
 * Example URLs:
 * - /deposit-ledger?user=123456789012345678901234
 * - /deposit-ledger?deliveryType=vending_machine&startDate=2024-01-01&endDate=2024-01-31
 * - /deposit-ledger?user=user123&latest=false&page=2
 */
const DepositLedger = ({ depositLedger, loading, getDepositLedgerAll, pages, pageList, activePage }) => {
    // Get location from React Router v6 hook
    const location = useLocation();
    const initialState = {
        startDate:'',
        endDate:'',
        deliveryType: '',
        latest: true
    };

    const [formData, setFormData] = useState(initialState);
    const [data, setData] = useState([]);
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');
    const [currentPage, setCurrentPage] = useState(1);
    const [queryString, setQueryString] = useState("page=1&latest=true");
    const [user, setUser] = useState("");

    const showAlert = (message, variant = 'danger') => {
        setToastMessage(message);
        setToastVariant(variant);
        setShowToast(true);
    };

    // Function to extract query parameters from URL
    const getQueryParams = (search) => {
        const params = new URLSearchParams(search);
        const queryParams = {};
        // Extract all query parameters
        for (const [key, value] of params.entries()) {
            queryParams[key] = value;
        }
        return queryParams;
    };

    // Function to build query string from parameters
    const buildQueryString = (params) => {
        const queryParts = [];
        // Add default parameters
        queryParts.push(`page=${params.page || 1}`);
        queryParts.push(`latest=${params.latest || true}`);
        // Add other parameters if they exist
        if (params.user) queryParts.push(`user=${params.user}`);
        if (params.deliveryType) queryParts.push(`deliveryType=${params.deliveryType}`);
        if (params.startDate) queryParts.push(`startDate=${params.startDate}`);
        if (params.endDate) queryParts.push(`endDate=${params.endDate}`);
        return queryParts.join('&');
    };

    useEffect(() => {
        if (depositLedger === undefined) {
            let query = queryString;
            if(location && location.search){
                // Extract all query parameters from URL
                const urlParams = getQueryParams(location.search);
                if (urlParams.user) {
                    setUser(urlParams.user);
                }
                // Update form data with URL parameters
                const updatedFormData = {
                    ...formData,
                    startDate: urlParams.startDate || '',
                    endDate: urlParams.endDate || '',
                    deliveryType: urlParams.deliveryType || '',
                    latest: urlParams.latest !== undefined ? urlParams.latest === 'true' : true
                };
                setFormData(updatedFormData);
                // Build query string from URL parameters
                query = buildQueryString(urlParams);
                setQueryString(query);
                // Call API
                getDepositLedgerAll(query);
            } else {
                getDepositLedgerAll(query);
            }
        } else {
            processLedgerData(depositLedger);
            setCurrentPage(activePage || 1);
        }
    }, [location, depositLedger, getDepositLedgerAll]);



    const processLedgerData = (ledgerData) => {
        const processedData = ledgerData.map(ledgerObj => ({
            _id: ledgerObj._id,
            user: ledgerObj.user ? ledgerObj.user.name : "Deleted User",
            orderId: ledgerObj.order ? ledgerObj.order.orderId : "",
            transactionId: ledgerObj.transaction ? ledgerObj.transaction.transactionId : "",
            deliveryType: ledgerObj.deliveryType,
            credit: ledgerObj.credit,
            debit: ledgerObj.debit,
            freeDeposit: ledgerObj.freeDeposit,
            amount: ledgerObj.amount,
            balance: ledgerObj.balance,
            message: ledgerObj.message,
            time: new Date(ledgerObj.time).toLocaleString(),
            rawTime: new Date(ledgerObj.time)
        }));

        // Sort by latest if needed
        if (formData.latest) {
            processedData.sort((a, b) => b.rawTime - a.rawTime);
        } else {
            processedData.sort((a, b) => a.rawTime - b.rawTime);
        }

        setData(processedData);
    };

    const {
        startDate,
        endDate,
        deliveryType,
        latest
    } = formData;

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const onSubmit = e => {
        e.preventDefault();
        
        if (formData.startDate && formData.endDate) {
            const startDate = new Date(formData.startDate).getTime();
            const endDate = new Date(formData.endDate).getTime();
            if(endDate < startDate){
                showAlert("End date must be after start date");
                return;
            }
        }
        
        // Build query parameters object
        const queryParams = {
            page: 1,
            latest: formData.latest,
            deliveryType: formData.deliveryType || undefined,
            startDate: formData.startDate || undefined,
            endDate: formData.endDate || undefined,
            user: user || undefined
        };
        // Build query string from parameters
        const newQueryString = buildQueryString(queryParams);
        setQueryString(newQueryString);
        setCurrentPage(1);
        getDepositLedgerAll(newQueryString);
    };

    const handleSort = (field) => {
        const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortDirection(direction);
        
        const sortedData = [...data].sort((a, b) => {
            if (direction === 'asc') {
                return a[field] > b[field] ? 1 : -1;
            }
            return a[field] < b[field] ? 1 : -1;
        });
        setData(sortedData);
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return <i className="fas fa-sort text-muted ms-1"></i>;
        return sortDirection === 'asc' 
            ? <i className="fas fa-sort-up text-primary ms-1"></i>
            : <i className="fas fa-sort-down text-primary ms-1"></i>;
    };

    const clearFilters = () => {
        setFormData(initialState);
        const queryParams = {
            page: 1,
            latest: true
        };
        const newQueryString = buildQueryString(queryParams);
        setQueryString(newQueryString);
        setCurrentPage(1);
        getDepositLedgerAll(newQueryString);
        showAlert("Filters cleared successfully!", "info");
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > pages) return;
        
        const newQueryString = queryString.replace(`page=${currentPage}`, `page=${page}`);
        setQueryString(newQueryString);
        setCurrentPage(page);
        
        getDepositLedgerAll(newQueryString);
    };

    const renderPagination = () => {
        if (!pages || pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(pages, startPage + maxVisiblePages - 1);

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        // Page numbers
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < pages) {
            if (endPage < pages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={pages} onClick={() => handlePageChange(pages)}>
                    {pages}
                </Pagination.Item>
            );
        }

        return (
            <div className="d-flex justify-content-center mt-4">
                <Pagination>
                    <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {items}
                    <Pagination.Next
                        disabled={currentPage === pages}
                        onClick={() => handlePageChange(currentPage + 1)}
                    />
                </Pagination>
            </div>
        );
    };

    const getDeliveryTypeBadge = (deliveryType) => {
        switch(deliveryType) {
            case 'vending_machine':
                return <Badge bg="primary">Vending Machine</Badge>;
            case 'delivery':
                return <Badge bg="success">Delivery</Badge>;
            case 'pickup':
                return <Badge bg="warning">Pickup</Badge>;
            default:
                return <Badge bg="secondary">{deliveryType}</Badge>;
        }
    };

    const renderDepositTable = () => {
        if (!data || data.length === 0) {
            return (
                <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No deposit ledger data found. Try adjusting your filters.
                </Alert>
            );
        }

        return (
            <div className="table-responsive">
                <Table striped bordered hover className="mb-0">
                    <thead className="table-dark">
                        <tr>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('orderId')}
                            >
                                Order/Transaction ID
                                {getSortIcon('orderId')}
                            </th>
                            <th
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('user')}
                            >
                                User
                                {getSortIcon('user')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('deliveryType')}
                            >
                                Delivery Type
                                {getSortIcon('deliveryType')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('credit')}
                            >
                                Credit
                                {getSortIcon('credit')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('debit')}
                            >
                                Debit
                                {getSortIcon('debit')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('freeDeposit')}
                            >
                                Free Deposit
                                {getSortIcon('freeDeposit')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('amount')}
                            >
                                Amount (₹)
                                {getSortIcon('amount')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('balance')}
                            >
                                Balance
                                {getSortIcon('balance')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('time')}
                            >
                                Time
                                {getSortIcon('time')}
                            </th>
                            <th>Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={row._id || index}>
                                <td>
                                    <div className="d-flex align-items-center">
                                        {row.orderId && (
                                            <Badge bg="success" className="me-2">O</Badge>
                                        )}
                                        {row.transactionId && (
                                            <Badge bg="primary" className="me-2">T</Badge>
                                        )}
                                        <span className="text-truncate" style={{maxWidth: '150px'}} title={row.orderId || row.transactionId || 'N/A'}>
                                            {row.orderId || row.transactionId || 'N/A'}
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <span className="text-truncate d-inline-block" style={{maxWidth: '150px'}} title={row.user}>
                                        <Badge bg="primary" className="me-2">
                                            {row.user}
                                        </Badge>
                                    </span>
                                </td>
                                <td>
                                    {getDeliveryTypeBadge(row.deliveryType)}
                                </td>
                                <td>
                                    {row.credit > 0 && (
                                        <span className="text-success fw-bold">
                                            <i className="fas fa-plus me-1"></i>
                                            {row.credit}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {row.debit > 0 && (
                                        <span className="text-danger fw-bold">
                                            <i className="fas fa-minus me-1"></i>
                                            {row.debit}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {row.freeDeposit > 0 && (
                                        <span className="text-info fw-bold">
                                            <i className="fas fa-gift me-1"></i>
                                            {row.freeDeposit}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    {row.amount > 0 && (
                                        <span className="text-success fw-bold">
                                            ₹{row.amount}
                                        </span>
                                    )}
                                </td>
                                <td>
                                    <span className={`fw-bold ${row.balance >= 0 ? 'text-success' : 'text-danger'}`}>
                                        {row.balance}
                                    </span>
                                </td>
                                <td>
                                    <small className="text-muted">
                                        {new Date(row.rawTime).toLocaleDateString()}<br/>
                                        {new Date(row.rawTime).toLocaleTimeString()}
                                    </small>
                                </td>
                                <td>
                                    <span className="text-truncate d-inline-block" style={{maxWidth: '250px'}} title={row.message}>
                                        {row.message}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <Container fluid className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="text-primary mb-0">
                    <i className="fas fa-piggy-bank me-2"></i>
                    Deposit Ledger
                </h2>
            </div>

            {/* Filters Card */}
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-filter me-2"></i>
                        Search Filters
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Row className="g-3">
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-truck me-2"></i>
                                        Delivery Type
                                    </Form.Label>
                                    <Form.Select
                                        name="deliveryType"
                                        value={deliveryType}
                                        onChange={onChange}
                                    >
                                        <option value="">All Delivery Types</option>
                                        <option value="vending_machine">Vending Machine</option>
                                        <option value="delivery">Delivery</option>
                                        <option value="pickup">Pickup</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-sort me-2"></i>
                                        Sort Order
                                    </Form.Label>
                                    <Form.Select
                                        name="latest"
                                        value={latest}
                                        onChange={onChange}
                                    >
                                        <option value="true">Newest First</option>
                                        <option value="false">Oldest First</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        Start Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={startDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        End Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="endDate"
                                        value={endDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12} lg={4} className="d-flex align-items-end">
                                <div className="d-flex gap-2 w-100">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={loading}
                                        className="flex-fill"
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Searching...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-search me-2"></i>
                                                Apply Filters
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        onClick={clearFilters}
                                        disabled={loading}
                                    >
                                        <i className="fas fa-times me-2"></i>
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Summary Cards */}
            {data.length > 0 && (
                <Row className="g-3 mb-4">
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-receipt text-primary fa-2x mb-2"></i>
                                <h5 className="card-title">Total Records</h5>
                                <h3 className="text-primary">{data.length}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-plus text-success fa-2x mb-2"></i>
                                <h5 className="card-title">Total Credits</h5>
                                <h3 className="text-success">
                                    {data.reduce((sum, row) => sum + row.credit, 0)}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-minus text-danger fa-2x mb-2"></i>
                                <h5 className="card-title">Total Debits</h5>
                                <h3 className="text-danger">
                                    {data.reduce((sum, row) => sum + row.debit, 0)}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-rupee-sign text-info fa-2x mb-2"></i>
                                <h5 className="card-title">Total Amount</h5>
                                <h3 className="text-info">
                                    ₹{data.reduce((sum, row) => sum + row.amount, 0)}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Results Card */}
            <Card className="shadow-sm">
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-table me-2"></i>
                        Deposit Records
                    </h5>
                    <div className="d-flex align-items-center gap-3">
                        {pages > 1 && (
                            <small className="text-light">
                                Page {currentPage} of {pages}
                            </small>
                        )}
                        {data.length > 0 && (
                            <Badge bg="light" text="dark">
                                {data.length} record{data.length !== 1 ? 's' : ''}
                            </Badge>
                        )}
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2">Loading deposit ledger data...</div>
                        </div>
                    ) : (
                        <>
                            {renderDepositTable()}
                            {/* Pagination */}
                            {pages > 1 && (
                                <div className="p-3 border-top bg-light">
                                    {renderPagination()}
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Toast Notifications */}
            <ToastContainer position="top-end" className="p-3">
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={5000}
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' : toastVariant === 'info' ? 'Info' : 'Alert'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toastVariant === 'danger' ? '' : 'text-white'}>
                        {toastMessage}
                    </Toast.Body>
                </Toast>
            </ToastContainer>

            <style>{`
                .cursor-pointer {
                    cursor: pointer;
                }
                .user-select-none {
                    user-select: none;
                }
                .table th:hover {
                    background-color: #495057 !important;
                }
            `}</style>
        </Container>
    )
}

DepositLedger.propTypes = {
    depositLedger: PropTypes.array,
    loading: PropTypes.bool,
    getDepositLedgerAll: PropTypes.func.isRequired,
    pages: PropTypes.number,
    pageList: PropTypes.array,
    activePage: PropTypes.number
}

const mapStateToProps = state => ({
    depositLedger: state.depositLedger.depositLedger,
    loading: state.depositLedger.loading,
    pages: state.depositLedger.pages,
    pageList: state.depositLedger.pageList,
    activePage: state.depositLedger.activePage
})

export default connect(mapStateToProps, { getDepositLedgerAll })(DepositLedger);